import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Check, Sparkles, Loader2, ArrowRight } from "lucide-react";
import api from "../lib/apiClient";
import { trackEvent, getVisitorId, getUtm } from "../lib/analytics";
export default function DiagnosticResult() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [consent, setConsent] = useState(false);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let stored = null;
    try { stored = JSON.parse(sessionStorage.getItem("cehnura_diag") || "null"); } catch { stored = null; }
    if (stored) { setD(stored); }
    else {
      // Reprise après abandon : récupère le dernier diagnostic du visiteur
      api.get(`/funnel/diagnostic/latest?visitor_id=${encodeURIComponent(getVisitorId())}`)
        .then((r) => { const data = r.data; setD(data); sessionStorage.setItem("cehnura_diag", JSON.stringify(data)); })
        .catch(() => setD(false));
    }
    trackEvent("diagnostic_result_view");
  }, []);

  if (d === false) return <div className="min-h-screen bg-[#0B0B0F] flex items-center justify-center"><Link to="/diagnostic" className="text-amber-400">Refaire le diagnostic</Link></div>;
  if (!d) return <div className="min-h-screen bg-[#0B0B0F] flex items-center justify-center"><Loader2 className="w-6 h-6 text-amber-500 animate-spin" /></div>;

  const submitLead = async () => {
    if (!email.includes("@")) return;
    setBusy(true);
    try {
      const utm = getUtm();
      await api.post("/funnel/lead", { visitor_id: getVisitorId(), email, first_name: firstName, profile: d.profile, consent_marketing: consent, diagnostic_id: d.id, utm_first: utm.first, utm_last: utm.last, ref: utm.ref });
      trackEvent("lead_created", { profile: d.profile });
      setSaved(true);
      sessionStorage.setItem("cehnura_lead_email", email);
    } catch { /* ignore */ } finally { setBusy(false); }
  };

  const goSignup = () => { trackEvent("signup_started"); navigate("/register"); };

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-zinc-100 px-5 py-10" data-testid="diagnostic-result-page">
      <div className="max-w-2xl mx-auto">
        <p className="text-xs uppercase tracking-wider text-amber-500 mb-2">Analyse de votre situation</p>
        <h1 className="font-display text-3xl font-extrabold mb-1" data-testid="result-label">{d.label}</h1>
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full gold-gradient" style={{ width: `${d.score}%` }} /></div>
          <span className="text-amber-400 font-bold" data-testid="result-score">{d.score}/100</span>
        </div>
        <p className="text-[11px] text-zinc-500 mb-6">Indicateur Cehnura basé uniquement sur vos réponses — ce n'est pas une mesure scientifique.</p>

        <Section title="Vos priorités" items={d.priorities} icon={<Sparkles className="w-4 h-4 text-amber-400" />} />
        <Section title="Vos opportunités" items={d.opportunities} icon={<Check className="w-4 h-4 text-emerald-400" />} />
        <Section title="Vos premières actions" items={d.actions} icon={<ArrowRight className="w-4 h-4 text-amber-400" />} />

        <div className="mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5" data-testid="result-locked">
          <div className="flex items-center gap-2 mb-1"><Lock className="w-4 h-4 text-amber-400" /><span className="font-semibold">Votre analyse complète</span></div>
          <p className="text-sm text-zinc-400">{d.locked_count} recommandations supplémentaires sont disponibles dans votre espace Cehnura.</p>
        </div>

        {!saved ? (
          <div className="mt-6 bg-[#15151B] border border-zinc-800 rounded-2xl p-5" data-testid="lead-form">
            <p className="text-sm text-zinc-300 mb-3">Recevez votre analyse et débloquez la suite :</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Prénom" data-testid="lead-firstname" className="bg-[#0F0F13] border border-zinc-800 rounded-xl h-11 px-3 text-sm outline-none focus:border-amber-500/50" />
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email" data-testid="lead-email" className="bg-[#0F0F13] border border-zinc-800 rounded-xl h-11 px-3 text-sm outline-none focus:border-amber-500/50" />
            </div>
            <label className="flex items-start gap-2 mt-3 text-xs text-zinc-500">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="lead-consent" className="mt-0.5" />
              J'accepte de recevoir des conseils et informations Cehnura par email (facultatif).
            </label>
            <button onClick={submitLead} disabled={busy || !email.includes("@")} data-testid="lead-submit"
              className="mt-4 w-full h-11 rounded-xl gold-gradient text-black font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Recevoir mon analyse"}
            </button>
          </div>
        ) : (
          <div className="mt-6 text-center">
            <p className="font-display text-xl font-bold mb-1">Votre prochaine étape commence maintenant.</p>
            <p className="text-sm text-zinc-400 mb-4">Créez gratuitement votre espace Cehnura et transformez cette analyse en plan d'action.</p>
          </div>
        )}

        <button onClick={goSignup} data-testid="result-cta-signup"
          className="mt-4 w-full h-12 rounded-xl gold-gradient text-black font-semibold inline-flex items-center justify-center gap-2">
          Créer mon compte gratuitement <ArrowRight className="w-4 h-4" />
        </button>
        <p className="text-center text-xs text-zinc-500 mt-3">Déjà inscrit ? <Link to="/login" className="text-amber-400">Se connecter</Link></p>
      </div>
    </div>
  );
}

function Section({ title, items, icon }) {
  return (
    <div className="mb-4">
      <h2 className="text-sm font-semibold text-zinc-300 mb-2">{title}</h2>
      <div className="space-y-2">
        {(items || []).map((it, k) => (
          <div key={k} className="flex items-start gap-2 bg-[#15151B] border border-zinc-800 rounded-xl px-4 py-3 text-sm">
            <span className="mt-0.5">{icon}</span>{it}
          </div>
        ))}
      </div>
    </div>
  );
}
